import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { NavigationMode } from './types';
import { ModeSelector } from './components/Navigation/ModeSelector';
import { HomeMode } from './components/Modes/HomeMode';
import { AmenitiesMode } from './components/Modes/AmenitiesMode';
import { SurroundingsMode } from './components/Modes/SurroundingsMode';
import { LotsMode } from './components/Modes/LotsMode';
import { InteriorApp } from './pages/InteriorApp';
import { ThemeProvider, useTheme } from './components/styles/ThemeContext';
function AppContent() {
  const [activeMode, setActiveMode] = useState<NavigationMode>('home');
  const [isCustomizing, setIsCustomizing] = useState(false);
  const [initialLotId, setInitialLotId] = useState<number | null>(null);
  const [isCustomizingInitial, setIsCustomizingInitial] = useState(false);
  const [isInteriorMode, setIsInteriorMode] = useState(false);
  const {
    isDark
  } = useTheme();
  // Handle URL routing on mount and popstate
  useEffect(() => {
    const handleUrlChange = () => {
      const path = window.location.pathname;
      // Check if we're in interior mode
      if (path.startsWith('/interior')) {
        setIsInteriorMode(true);
        return;
      }
      setIsInteriorMode(false);
      if (path === '/' || path === '/home') {
        setActiveMode('home');
      } else if (path === '/amenities') {
        setActiveMode('amenities');
      } else if (path === '/nearby') {
        setActiveMode('surroundings');
      } else if (path.startsWith('/lots')) {
        setActiveMode('lots');
        // Parse lot ID and customize state
        const parts = path.split('/');
        if (parts.length >= 3) {
          const lotId = parseInt(parts[2]);
          if (!isNaN(lotId)) {
            setInitialLotId(lotId);
            if (parts[3] === 'customize') {
              setIsCustomizingInitial(true);
              setIsCustomizing(true);
            } else {
              setIsCustomizingInitial(false);
              setIsCustomizing(false);
            }
          }
        } else {
          setInitialLotId(null);
          setIsCustomizingInitial(false);
          setIsCustomizing(false);
        }
      }
    };
    // Initial check
    handleUrlChange();
    // Listen for popstate (back/forward button)
    window.addEventListener('popstate', handleUrlChange);
    return () => window.removeEventListener('popstate', handleUrlChange);
  }, []);
  const handleModeSelect = (mode: NavigationMode) => {
    // If clicking the same mode (lots), dispatch reselect event to reopen filters
    if (mode === activeMode && mode === 'lots') {
      window.dispatchEvent(new CustomEvent('lotsmode-reselect'));
    } else {
      setActiveMode(mode);
      // Update URL based on mode
      let path = '/';
      switch (mode) {
        case 'home':
          path = '/';
          break;
        case 'amenities':
          path = '/amenities';
          break;
        case 'surroundings':
          path = '/nearby';
          break;
        case 'lots':
          path = '/lots';
          break;
      }
      window.history.pushState({}, '', path);
    }
  };
  // Render interior app if in interior mode
  if (isInteriorMode) {
    return <InteriorApp data-id="element-0" />;
  }
  // Render exterior subdivision app
  return <div className={`relative w-full h-screen overflow-hidden font-sans transition-colors duration-300 ${isDark ? 'bg-slate-900 text-white' : 'bg-gray-100 text-gray-900'}`} data-id="element-1">
      {/* Background 3D Simulation */}
      <div className="absolute inset-0 z-0" data-id="element-2">
        <img src="https://cdn.magicpatterns.com/uploads/5puBkgd7BsZRrBGtZRzFYT/image.png" alt="Aerial Subdivision View" className="w-full h-full object-cover transition-transform duration-[2000ms] ease-in-out" style={{
        transform: activeMode === 'home' ? 'scale(1)' : 'scale(1.1)'
      }} data-id="element-3" />
        <div className={`absolute inset-0 transition-all duration-300 ${isDark ? 'bg-gradient-to-b from-slate-900/30 to-slate-900/10' : 'bg-gradient-to-b from-white/20 to-white/5'} ${activeMode === 'home' ? 'opacity-40' : 'opacity-20'}`} data-id="element-4" />
      </div>

      {/* Primary Navigation (Left) */}
      <ModeSelector activeMode={activeMode} onSelectMode={handleModeSelect} data-id="element-5" />

      {/* Mode Content Layer */}
      <div className="absolute inset-0 z-10 pointer-events-none" data-id="element-6">
        <div className="relative w-full h-full pointer-events-auto" data-id="element-7">
          <AnimatePresence mode="wait" data-id="element-8">
            {activeMode === 'home' && <motion.div key="home" className="absolute inset-0" exit={{
            opacity: 0
          }} transition={{
            duration: 0.3
          }} data-id="element-9">
                <HomeMode data-id="element-10" />
              </motion.div>}
            {activeMode === 'amenities' && <motion.div key="amenities" className="absolute inset-0" exit={{
            opacity: 0
          }} transition={{
            duration: 0.3
          }} data-id="element-11">
                <AmenitiesMode data-id="element-12" />
              </motion.div>}
            {activeMode === 'surroundings' && <motion.div key="surroundings" className="absolute inset-0" exit={{
            opacity: 0
          }} transition={{
            duration: 0.3
          }} data-id="element-13">
                <SurroundingsMode data-id="element-14" />
              </motion.div>}
            {activeMode === 'lots' && <motion.div key="lots" className="absolute inset-0" exit={{
            opacity: 0
          }} transition={{
            duration: 0.3
          }} data-id="element-15">
                <LotsMode onCustomizingChange={setIsCustomizing} initialLotId={initialLotId} isCustomizingInitial={isCustomizingInitial} data-id="element-16" />
              </motion.div>}
          </AnimatePresence>
        </div>
      </div>
    </div>;
}
export function App() {
  return <ThemeProvider data-id="element-17">
      <AppContent data-id="element-18" />
    </ThemeProvider>;
}